import {
  normalizeOrganizationId,
  type OrganizationId,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { indexTableQueryOptions } from '@/lib/services/index-table-query-options'
import { conditionalObjectMerge } from '@/lib/utils'
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
  type QueryClient,
  type UseQueryOptions,
  type UseSuspenseQueryOptions,
} from '@tanstack/react-query'
import {
  createOrganization,
  deleteOrganization,
  getOrganization,
  getOrganizationLifecycleReadiness,
  getOrganizationRepresentatives,
  getOrganizations,
  getOrgOnboardingStatus,
  invalidateOrganizationStorefrontCache,
  markOrganizationLive,
  markOrganizationLost,
  updateOrganization,
} from './organizations.api'
import { useParams } from '@tanstack/react-router'
import type { OrganizationFilters } from './organization.schema'
import type {
  Organization,
  OrganizationLifecycleReadiness,
  OrganizationOnboardingStatus,
  OrganizationRepresentative,
  UpdateOrganizationInput,
} from './organizations.types'

interface OrganizationQueryRoot {
  entity: 'organization'
}

export type OrganizationIndexQueryKey = [
  OrganizationQueryRoot & { scope: 'index'; filters?: OrganizationFilters },
]

/**
 * The key builders take a nullable id because cache reads and writes can lack one; the fetchers
 * require it before it reaches the URL.
 */
export type OrganizationIdQueryKey = [
  OrganizationQueryRoot & {
    scope: 'show' | 'onboarding' | 'lifecycle-readiness'
    organizationId?: number
  },
]

type OrganizationRepresentativesQueryKey = [OrganizationQueryRoot & { scope: 'representatives' }]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TData, TQueryKey extends readonly unknown[]> = Partial<
  Omit<UseQueryOptions<TData, Error, TData, TQueryKey>, 'queryKey' | 'queryFn'>
>

type SuspenseQueryParams<TData, TQueryKey extends readonly unknown[]> = Partial<
  Omit<UseSuspenseQueryOptions<TData, Error, TData, TQueryKey>, 'queryKey' | 'queryFn'>
>

const organizationQueryRoot: OrganizationQueryRoot = { entity: 'organization' }

export const organizationQueryKeys = {
  all: [organizationQueryRoot] satisfies [OrganizationQueryRoot],
  index: (filters?: OrganizationFilters): OrganizationIndexQueryKey => [
    conditionalObjectMerge({ filters }, { ...organizationQueryKeys.all[0], scope: 'index' }),
  ],
  show: (organizationId: OrganizationIdInput): OrganizationIdQueryKey => [
    conditionalObjectMerge(
      { organizationId: normalizeOrganizationId(organizationId) },
      { ...organizationQueryKeys.all[0], scope: 'show' }
    ),
  ],
  onboarding: (organizationId: OrganizationIdInput): OrganizationIdQueryKey => [
    conditionalObjectMerge(
      { organizationId: normalizeOrganizationId(organizationId) },
      { ...organizationQueryKeys.all[0], scope: 'onboarding' }
    ),
  ],
  lifecycleReadiness: (organizationId: OrganizationIdInput): OrganizationIdQueryKey => [
    conditionalObjectMerge(
      { organizationId: normalizeOrganizationId(organizationId) },
      { ...organizationQueryKeys.all[0], scope: 'lifecycle-readiness' }
    ),
  ],
  representatives: (): OrganizationRepresentativesQueryKey => [
    { ...organizationQueryKeys.all[0], scope: 'representatives' },
  ],
}

export const organizationQueries = {
  index: (filters?: OrganizationFilters) =>
    indexTableQueryOptions({
      queryKey: organizationQueryKeys.index(filters),
      queryFn: getOrganizations,
    }),

  show: (organizationId: OrganizationIdInput) =>
    queryOptions({
      queryKey: organizationQueryKeys.show(organizationId),
      queryFn: getOrganization,
    }),
  onboarding: (organizationId: OrganizationIdInput) =>
    queryOptions({
      queryKey: organizationQueryKeys.onboarding(organizationId),
      queryFn: getOrgOnboardingStatus,
      staleTime: 1000 * 3, // small stale time so we don't have to invalidate all the time
    }),
  lifecycleReadiness: (organizationId: OrganizationIdInput) =>
    queryOptions({
      queryKey: organizationQueryKeys.lifecycleReadiness(organizationId),
      queryFn: getOrganizationLifecycleReadiness,
      staleTime: 0,
    }),
  representatives: () =>
    queryOptions({
      queryKey: organizationQueryKeys.representatives(),
      queryFn: getOrganizationRepresentatives,
    }),
}

export function useOrganizations({
  filters,
  params = {},
}: {
  filters?: OrganizationFilters
  params?: QueryParams<Organization[], OrganizationIndexQueryKey>
} = {}) {
  return useQuery({
    ...organizationQueries.index(filters),
    ...params,
  })
}

export function useOrganization(
  organizationId: OrganizationIdInput,
  params: SuspenseQueryParams<Organization, OrganizationIdQueryKey> = {}
) {
  return useSuspenseQuery({
    ...organizationQueries.show(organizationId),
    ...params,
  })
}

export function useOrgOnboardingStatus(
  organizationId: OrganizationIdInput,
  params: SuspenseQueryParams<OrganizationOnboardingStatus, OrganizationIdQueryKey> = {}
) {
  return useSuspenseQuery({
    ...organizationQueries.onboarding(organizationId),
    ...params,
  })
}

export function useOrganizationLifecycleReadiness(
  organizationId: OrganizationIdInput,
  params: QueryParams<OrganizationLifecycleReadiness, OrganizationIdQueryKey> = {}
) {
  return useQuery({
    ...organizationQueries.lifecycleReadiness(organizationId),
    ...params,
  })
}

export function useOrganizationRepresentatives(
  params: QueryParams<OrganizationRepresentative[], OrganizationRepresentativesQueryKey> = {}
) {
  return useQuery({
    ...organizationQueries.representatives(),
    ...params,
  })
}

export function useCreateOrganizationMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createOrganization,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: organizationQueryKeys.all }),
  })
}

export function useUpdateOrganizationMutation() {
  const queryClient = useQueryClient()
  // Route params are untyped while route files are JavaScript; narrow `$orgId` here.
  const params: unknown = useParams({ strict: false })
  const orgId =
    typeof params === 'object' && params !== null && 'orgId' in params ? params.orgId : undefined
  const currentOrganizationId = normalizeOrganizationId(
    typeof orgId === 'number' || typeof orgId === 'string' ? orgId : undefined
  )

  return useMutation({
    mutationFn: ({ id, ...values }: UpdateOrganizationInput & { id: OrganizationId }) =>
      updateOrganization(id, values),
    onSuccess: async (updatedOrg) => {
      const isCurrentOrg = updatedOrg?.id === currentOrganizationId

      // Updating another organization invalidates every cached query. This was meant to skip the
      // current organization's queries, but the predicate was passed as a bare function, which
      // TanStack reads as empty filters; narrowing it is a deliberate behavior change for later.
      return isCurrentOrg
        ? queryClient.invalidateQueries({ queryKey: organizationQueryKeys.all })
        : queryClient.invalidateQueries()
    },
  })
}

function useOrganizationLifecycleMutation<TData>(
  mutationFn: (organizationId: OrganizationId) => Promise<TData>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: organizationQueryKeys.all }),
  })
}

export function useMarkOrganizationLostMutation() {
  return useOrganizationLifecycleMutation(markOrganizationLost)
}

export function useMarkOrganizationLiveMutation() {
  return useOrganizationLifecycleMutation(markOrganizationLive)
}

const cleanupDeletedOrganizationQueries = ({
  queryClient,
  orgId,
}: {
  queryClient: QueryClient
  orgId: OrganizationIdInput
}) => {
  queryClient.removeQueries({ queryKey: organizationQueryKeys.show(orgId) })

  return queryClient.invalidateQueries({ queryKey: organizationQueryKeys.all })
}

export function useDeleteOrganizationMutation({
  cleanupOnSuccess = true,
}: { cleanupOnSuccess?: boolean } = {}) {
  const queryClient = useQueryClient()
  const cleanupDeletedOrganization = (orgId: OrganizationIdInput) =>
    cleanupDeletedOrganizationQueries({ queryClient, orgId })

  const mutation = useMutation({
    mutationFn: deleteOrganization,
    onSuccess: (_, orgId) => {
      if (!cleanupOnSuccess) {
        return undefined
      }

      return cleanupDeletedOrganization(orgId)
    },
  })

  return {
    ...mutation,
    cleanupDeletedOrganization,
  }
}

export function useInvalidateStorefrontCacheMutation() {
  return useMutation({
    mutationFn: (organizationId: OrganizationId) =>
      invalidateOrganizationStorefrontCache(organizationId),
  })
}
